import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Card, CardContent, Tooltip, Typography } from '@mui/material';
import type { PropsWithChildren, ReactNode } from 'react';

import { retentionHelpTooltipComponentsProps } from 'src/components/Retention/helpTooltipProps';
import palette from 'src/theme/palette';

interface SectionCardProps extends PropsWithChildren {
  title: string;
  tooltip: string;
  action?: ReactNode;
}

export function SectionCard({
  title,
  tooltip,
  action,
  children,
}: SectionCardProps) {
  return (
    <Card
      data-retention-pdf-block="section-card"
      sx={{
        height: '100%',
        overflow: 'visible',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'rgba(15, 23, 42, 0.06)',
        boxShadow: '0 12px 34px -22px rgba(15, 23, 42, 0.24)',
        transition: 'box-shadow 180ms ease, transform 180ms ease',
        '&:hover': {
          boxShadow: '0 18px 40px -24px rgba(15, 23, 42, 0.28)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardContent
        sx={{
          p: 2.25,
          overflow: 'visible',
          '&:last-child': { pb: 2.25 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
            pb: 1.25,
            borderBottom: '1px solid',
            borderColor: palette.grey[200],
          }}
        >
          <Box display="flex" alignItems="center" gap={0.75}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: palette.text.primary }}
            >
              {title}
            </Typography>
            <Tooltip
              title={tooltip}
              arrow
              placement="top"
              enterTouchDelay={0}
              componentsProps={retentionHelpTooltipComponentsProps}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: palette.text.secondary,
                  cursor: 'help',
                }}
              />
            </Tooltip>
          </Box>
          {action}
        </Box>
        <Box sx={{ width: '100%', minWidth: 0, overflow: 'visible' }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );
}

export default SectionCard;
